import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';

import {
    formatHiringCityFilterLabel,
    hiringCityFilterFromPlaceDetails,
} from './hiringMockData';
import { fetchAddressPlaceDetails, fetchAddressSuggestions } from './restaurantApi';

const ADDRESS_SEARCH_DEBOUNCE_MS = 350;
const CITY_SUGGEST_TYPES = '(cities)';

/**
 * Single-field city search (Google Places) — filters applicants by city.
 * value: null | { city, label? }
 */
export default function HiringLocationAddressFilter({
    value = null,
    onChange,
    placeholder = 'Search city…',
}) {
    const [query, setQuery] = useState(() => formatHiringCityFilterLabel(value));
    const [suggestions, setSuggestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const debounceRef = useRef(null);
    const wrapRef = useRef(null);

    useEffect(() => {
        setQuery(formatHiringCityFilterLabel(value));
    }, [value]);

    useEffect(() => {
        const onPointerDown = (event) => {
            if (wrapRef.current && !wrapRef.current.contains(event.target)) {
                setSuggestions([]);
            }
        };
        document.addEventListener('mousedown', onPointerDown);
        return () => document.removeEventListener('mousedown', onPointerDown);
    }, []);

    useEffect(
        () => () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        },
        []
    );

    const clearFilter = useCallback(() => {
        setQuery('');
        setSuggestions([]);
        onChange?.(null);
    }, [onChange]);

    const handleQueryChange = (e) => {
        const next = e.target.value;
        setQuery(next);
        if (value) onChange?.(null);

        if (debounceRef.current) clearTimeout(debounceRef.current);

        const trimmed = next.trim();
        if (trimmed.length < 2) {
            setSuggestions([]);
            setLoading(false);
            return;
        }

        debounceRef.current = setTimeout(async () => {
            debounceRef.current = null;
            try {
                setLoading(true);
                const list = await fetchAddressSuggestions(trimmed, {
                    types: CITY_SUGGEST_TYPES,
                });
                setSuggestions(list);
            } catch {
                setSuggestions([]);
            } finally {
                setLoading(false);
            }
        }, ADDRESS_SEARCH_DEBOUNCE_MS);
    };

    const handleSelectSuggestion = async (suggestion) => {
        const placeId = suggestion.place_id ?? suggestion.placeId;
        const description =
            suggestion.description ?? suggestion.formatted_address ?? '';
        if (!placeId) return;

        setSuggestions([]);
        setLoading(true);
        try {
            const details = await fetchAddressPlaceDetails(placeId);
            const filter = hiringCityFilterFromPlaceDetails(details);
            if (filter) {
                setQuery(filter.city);
                onChange?.({ city: filter.city, label: filter.city });
            } else {
                const city = description.split(',')[0]?.trim() || description;
                setQuery(city);
                onChange?.({ city, label: city });
            }
        } catch {
            const city = description.split(',')[0]?.trim() || description;
            setQuery(city);
            onChange?.({ city, label: city });
        } finally {
            setLoading(false);
        }
    };

    const showClear = query.trim() !== '' || value;

    return (
        <div
            ref={wrapRef}
            className="menu-food-filter-wrap hiring-search-wrap hiring-location-address-filter"
        >
            <input
                type="text"
                inputMode="search"
                autoComplete="off"
                className="menu-food-filter-input"
                placeholder={placeholder}
                value={query}
                onChange={handleQueryChange}
                aria-label="Search city"
                role="combobox"
                aria-expanded={suggestions.length > 0}
                aria-autocomplete="list"
            />
            {loading ? (
                <span className="hiring-location-address-loading" aria-hidden>
                    …
                </span>
            ) : null}
            {showClear ? (
                <button
                    type="button"
                    className="menu-food-filter-clear"
                    aria-label="Clear city"
                    onClick={clearFilter}
                >
                    <FontAwesomeIcon icon={faXmark} />
                </button>
            ) : null}
            {suggestions.length > 0 ? (
                <ul className="hiring-location-suggestions" role="listbox">
                    {suggestions.map((suggestion, index) => {
                        const placeId = suggestion.place_id ?? suggestion.placeId;
                        const text =
                            suggestion.description ??
                            suggestion.formatted_address ??
                            '';
                        return (
                            <li key={placeId || index} role="option">
                                <button
                                    type="button"
                                    className="hiring-location-suggestion-btn"
                                    onClick={() => handleSelectSuggestion(suggestion)}
                                >
                                    {text}
                                </button>
                            </li>
                        );
                    })}
                </ul>
            ) : null}
        </div>
    );
}
